import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import CustomerDetailsForm from '../components/checkout/CustomerDetailsForm';
import OrderSummary from '../components/checkout/OrderSummary';
import PaymentModeSelector from '../components/checkout/PaymentModeSelector';
import useRazorpayCheckout, {
  parsePriceAmount,
  loadRazorpayScript,
  createPaymentOrder,
  verifyPayment,
} from '../hooks/useRazorpayCheckout';

const INITIAL_DETAILS = { name: '', email: '', phone: '' };

const buildRazorpayOptions = ({
  order,
  program,
  amount,
  paymentMode,
  installmentNumber,
  customerDetails,
  onSuccess,
}) => ({
  key: order.key_id,
  amount: order.amount,
  currency: order.currency,
  name: 'ZEEVA Life',
  description: program.name,
  order_id: order.order_id,
  prefill: {
    name: customerDetails.name,
    email: customerDetails.email,
    contact: customerDetails.phone,
  },
  theme: { color: '#1DB954' },
  handler: async (response) => {
    try {
      const verifyResponse = await verifyPayment({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        customer_details: customerDetails,
        program_details: {
          name: program.name,
          amount,
          installment: paymentMode === 'installment',
          installment_number: installmentNumber,
        },
      });
      if (verifyResponse.data.success) {
        onSuccess(response);
      }
    } catch (err) {
      toast.error(
        'Payment verification failed. Please contact support with your payment ID.'
      );
    }
  },
});

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { program } = location.state || {};

  const [customerDetails, setCustomerDetails] = useState(INITIAL_DETAILS);
  const [paymentMode, setPaymentMode] = useState('full');
  const [loading, setLoading] = useState(false);
  const { installmentData } = useRazorpayCheckout(program);

  useEffect(() => {
    if (!program) {
      navigate('/');
      return;
    }
    // Custom-priced programs cannot use Razorpay checkout — route back
    if (parsePriceAmount(program.price) === 0) {
      toast.info('This program uses a custom investment. Please book a consultation.');
      navigate(`/pricing/${program.id}`);
    }
  }, [program, navigate]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setCustomerDetails((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePaymentSuccess = useCallback(
    (response, { amount, installmentNumber }) => {
      navigate('/payment-success', {
        state: {
          payment_id: response.razorpay_payment_id,
          order_id: response.razorpay_order_id,
          customer: customerDetails,
          program,
          amount,
          installment: paymentMode === 'installment',
          installment_number: installmentNumber,
        },
      });
    },
    [customerDetails, program, paymentMode, navigate]
  );

  const handlePayment = async () => {
    if (!customerDetails.name || !customerDetails.email || !customerDetails.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      toast.error('Failed to load payment gateway. Please try again.');
      setLoading(false);
      return;
    }

    const fullAmount = parsePriceAmount(program.price);
    const isInstallment = paymentMode === 'installment' && installmentData;
    const amount = isInstallment ? installmentData.installment_1 : fullAmount;
    const installmentNumber = isInstallment ? 1 : null;

    try {
      const orderResponse = await createPaymentOrder({
        amount,
        program,
        customerDetails,
        paymentMode,
        installmentNumber,
      });

      const options = buildRazorpayOptions({
        order: orderResponse.data,
        program,
        amount,
        paymentMode,
        installmentNumber,
        customerDetails,
        onSuccess: (resp) =>
          handlePaymentSuccess(resp, { amount, installmentNumber }),
      });

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        'Failed to initiate payment. Please try again or contact support.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!program) {
    return null;
  }

  const fullAmount = parsePriceAmount(program.price);
  const buttonAmount =
    paymentMode === 'installment' && installmentData
      ? installmentData.installment_1
      : fullAmount;

  return (
    <div
      className="min-h-screen bg-[#1E2740]"
      style={{ paddingTop: '100px' }}
      data-testid="checkout-page"
    >
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft size={20} aria-hidden="true" />
          <span>Back</span>
        </button>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-black mb-8 bg-gradient-to-r from-white via-brand-primary to-brand-accent bg-clip-text text-transparent text-center">
            Checkout
          </h1>

          <div className="grid md:grid-cols-2 gap-8">
            <CustomerDetailsForm
              customerDetails={customerDetails}
              onChange={handleInputChange}
            />

            <div className="space-y-6">
              <OrderSummary program={program} fullAmount={fullAmount} />

              <PaymentModeSelector
                paymentMode={paymentMode}
                onChangeMode={setPaymentMode}
                fullAmount={fullAmount}
                installmentData={installmentData}
              />

              <button
                onClick={handlePayment}
                disabled={loading}
                className="btn-cta w-full text-lg py-4"
                data-testid="checkout-pay-btn"
              >
                {loading
                  ? 'Processing...'
                  : `Proceed to Pay ₹${buttonAmount.toLocaleString('en-IN')}`}
              </button>

              <p className="text-center text-text-muted text-sm">
                Secure payment powered by Razorpay
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
